import { get as getStore, writable } from 'svelte/store'

// create will create a new router object based on the given object fields.
export function create ({ host = '', pathname = '', query = '', hash = '' }, missing = '') {
  pathname = createPathname(pathname)
  query = createQuery(query)
  hash = createHash(hash)

  const router = {
    hooks: {
      befores: [],
      afters: [],
    },
    url: writable({
      host,
      pathname,
      query,
      params: new URLSearchParams(query),
      hash,
      full: pathname + query + hash,
      absolute: `https://${host}${pathname}${query}${hash}`,
    }),
    get host () {
      return getStore(router.url).host
    },
    get pathname () {
      return getStore(router.url).pathname
    },
    get query () {
      return getStore(router.url).query
    },
    get params () {
      return getStore(router.url).params
    },
    paramsAsObject () {
      const obj = {}

      for (const [param, value] of router.params.entries()) {
        obj[param] = obj[param] || []

        obj[param].push(value)
      }

      return obj
    },
    get hash () {
      return getStore(router.url).hash
    },
    get full () {
      return getStore(router.url).full
    },
    get absolute () {
      return getStore(router.url).absolute
    },
    before (func) {
      if (typeof func !== 'function' || router.hooks.befores.includes(func)) {
        return
      }

      router.hooks.befores.push(func)

      return () => {
        router.hooks.befores = router.hooks.befores.filter(f => f !== func)
      }
    },
    after (func) {
      if (typeof func !== 'function' || router.hooks.afters.includes(func)) {
        return
      }

      router.hooks.afters.push(func)

      return () => {
        router.hooks.afters = router.hooks.afters.filter(f => f !== func)
      }
    },
    async select ({ routes = [], pathname = '' }) {
      pathname = pathname || router.pathname

      let selected = null
      let catchAll = null
      let route = ''

      for (const [pattern, ref] of routes) {
        if (pattern === '*') {
          catchAll = ref
          route = '*'

          continue
        }

        if (pattern.endsWith('/*')) {
          if (pathname.startsWith(pattern.substr(0, pattern.length - 1))) {
            selected = ref
            route = pattern
          }
        } else if (pathname === pattern) {
          selected = ref
          route = pattern
        }

        if (selected) {
          break
        }
      }

      if (!selected) {
        selected = catchAll
      }

      if (!selected) {
        selected = missing
      }

      return { route, ref: selected }
    },
    async scrollRestoration (scrollRestoration) {
      if (!IS_BROWSER) {
        return
      }

      if ('scrollRestoration' in history) {
        history.scrollRestoration = scrollRestoration
      }
    },
    scrollTo (top, offset = 0) {
      if (!IS_BROWSER) {
        return
      }

      setTimeout(() => {
        if (typeof top === 'string') {
          const node = document.querySelector(top)

          if (!node) {
            console.warn(`router scroll to: could not find a node using "${top}"`)

            return
          }

          const rect = node.getBoundingClientRect()

          top = rect.top + window.scrollY
        }

        if (typeof top !== 'number') {
          console.warn('router scroll to: top must be a number')

          return
        }

        if (typeof offset !== 'number') {
          offset = 0
        }

        scrollTo(top + offset)
      }, 0)
    },
    async replace (...args) {
      router._navigate('replace', ...args)
    },
    async push (...args) {
      router._navigate('push', ...args)
    },
    async _navigate (kind, url, options = {}) {
      if (options.basic) {
        options.skipHooks = true
        options.noScroll = true
      }

      if (options.scrollTo) {
        options.noScroll = true
        options.scrollToOffset = Number(options.scrollToOffset || 0)
      } else {
        options.scrollTo = null
      }

      const current = JSON.parse(JSON.stringify(getStore(router.url)))

      if (url instanceof URLSearchParams) {
        url = `${current.pathname}?${url.toString()}`
      }

      if (typeof url === 'string') {
        if (url.startsWith('http://') || url.startsWith('https://')) {
          window.open(url, '_blank')

          return true
        }

        const [start, hash] = url.split('#')
        const [pathname, query] = (start || '').split('?')

        url = { pathname, query, hash }
      }

      if (IS_BROWSER) {
        url.pathname = (url.pathname || current.pathname).replace(`${location.protocol}//${location.host}`, '')
      }

      url.pathname = createPathname(url.pathname)
      url.query = createQuery(url.query || '')
      url.hash = createHash(url.hash || '')
      url.params = new URLSearchParams(url.query)

      const isSameResource = url.pathname === current.pathname && url.query === current.query

      if (isSameResource && url.hash === current.hash) {
        if (options.scrollTo != null) {
          router.scrollTo(options.scrollTo, options.scrollToOffset)
        } else if (!options.noScroll) {
          const wentToHash = await goToHash()

          if (!wentToHash) {
            location.reload()
          }
        }

        return true
      }

      if (!options.skipHooks) {
        for (const func of router.hooks.befores) {
          const urlCopy = JSON.parse(JSON.stringify(url))
          const currentCopy = JSON.parse(JSON.stringify(current))

          urlCopy.params = new URLSearchParams(urlCopy.query)
          urlCopy.full = urlCopy.pathname + urlCopy.query + urlCopy.hash

          currentCopy.params = new URLSearchParams(currentCopy.query)
          currentCopy.full = currentCopy.pathname + currentCopy.query + currentCopy.hash

          const res = await func(urlCopy, currentCopy)

          if (res === false) {
            return false
          } else if (res && res.redirect) {
            return await router.push(res.redirect)
          }
        }
      }

      router.url.update(store => {
        let full = url.pathname + url.query + url.hash

        if (options.decodeParams) {
          full = decodeURIComponent(full)
        }

        store.pathname = url.pathname
        store.query = url.query
        store.params = url.params
        store.hash = url.hash
        store.full = full
        store.absolute = `https://${router.host}${full}`

        return store
      })

      if (IS_BROWSER) {
        if (kind === 'push') {
          history.pushState(null, '', router.full)
        } else {
          history.replaceState(null, '', router.full)
        }
      }

      if (!options.skipHooks) {
        for (const func of router.hooks.afters) {
          const urlCopy = JSON.parse(JSON.stringify(url))
          const currentCopy = JSON.parse(JSON.stringify(current))

          urlCopy.params = new URLSearchParams(urlCopy.query)
          currentCopy.params = new URLSearchParams(currentCopy.query)

          await func(urlCopy, currentCopy)
        }
      }

      if (options.scrollTo != null) {
        router.scrollTo(options.scrollTo, options.scrollToOffset)
      } else if (!options.noScroll) {
        const wentToHash = await goToHash()

        if (!wentToHash && !isSameResource) {
          router.scrollTo(0)
        }
      }

      return true
    },
    toString () {
      return router.full
    },
  }

  if (IS_BROWSER) {
    // We need to react to the forward/back buttons in the browser being pressed so we can update the
    // url field in the store
    window.addEventListener('popstate', async () => {
      const current = JSON.parse(JSON.stringify(getStore(router.url)))
      const { pathname, search, hash } = location
      const url = {
        pathname,
        query: search,
        params: new URLSearchParams(search),
        hash,
      }

      for (const func of router.hooks.befores) {
        const urlCopy = JSON.parse(JSON.stringify(url))
        const currentCopy = JSON.parse(JSON.stringify(current))

        urlCopy.params = new URLSearchParams(urlCopy.query)
        currentCopy.params = new URLSearchParams(currentCopy.query)

        const res = await func(urlCopy, currentCopy)

        if (res === false) {
          return false
        } else if (res && res.redirect) {
          return await router.push(res.redirect)
        }
      }

      router.url.update(store => {
        const full = url.pathname + url.query + url.hash

        store.pathname = url.pathname
        store.query = url.search
        store.params = url.params
        store.hash = url.hash
        store.full = full
        store.absolute = `https://${router.host}${full}`

        return store
      })

      for (const func of router.hooks.afters) {
        const urlCopy = JSON.parse(JSON.stringify(url))
        const currentCopy = JSON.parse(JSON.stringify(current))

        urlCopy.params = new URLSearchParams(urlCopy.query)
        currentCopy.params = new URLSearchParams(currentCopy.query)

        await func(urlCopy, currentCopy)
      }

      await goToHash()
    })

    // We need to monkey-patch the history.pushState function so we can react to calls, since there is no listener we
    // can set in the same way we can set a popstate listener for the forward/back navigation buttons
    const pushState = history.pushState

    history.pushState = function (state, title, url) {
      // Call the original pushState function making sure to set the context to history again
      // If we don't use the call() method here then the history context will be lost and it won't work
      pushState.call(history, state, title, url)
    }

    // We need to monkey-patch the history.replaceState function so we can react to calls, since there is no listener we
    // can set in the same way we can set a popstate listener for the forward/back navigation buttons
    const replaceState = history.replaceState

    history.replaceState = function (state, title, url) {
      // Call the original replaceState function making sure to set the context to history again
      // If we don't use the call() method here then the history context will be lost and it won't work
      replaceState.call(history, state, title, url)
    }

    document.addEventListener('click', async e => {
      const target = e.target.closest('a')

      if (!target) {
        return
      }

      const { tagName, target: targetAttr } = target
      const isDownload = target.getAttribute('download') != null
      const fullReload = target.getAttribute('data-full-reload') != null
      const scrollTo = String(target.getAttribute('data-scroll-to') || '')
      const scrollToOffset = Number(target.getAttribute('data-scroll-to-offset') || 0)
      const noScroll = target.getAttribute('data-no-scroll') != null
      const skipHooks = target.getAttribute('data-skip-hooks') != null
      const basic = target.getAttribute('data-basic') != null
      let { href } = target

      href = (href || '').replace(`${location.protocol}//${location.host}`, '')

      let isPossibleFile = false
      const basename = href.split('/').pop()

      if (basename.includes('.')) {
        // The current test for a possible file is to see if the basename has an extension that's 4 characters
        // in length or less
        // This should cover most file URLs, but can be changed as a later date if we run into problems
        // The main reason for doing it this way is to avoid writing a large list of valid extensions to test against
        isPossibleFile = basename.split('.').pop().length <= 4
      }

      if (
        tagName !== 'A' ||
        targetAttr === '_blank' ||
        isDownload ||
        fullReload ||
        href.startsWith('http://') ||
        href.startsWith('https://') ||
        href.startsWith('tel:') ||
        href.startsWith('mailto:') ||
        target.getAttribute('href') === '#' ||
        isPossibleFile
      ) {
        return
      }

      e.preventDefault()

      await router.push(href, {
        basic,
        noScroll,
        skipHooks,
        scrollTo,
        scrollToOffset,
      })
    })
  }

  return router
}

// createPath name will return a pathname string based on the one given to it.
// The returned string will always be prefixed with a /.
// An empty pathname will always be returned as /.
function createPathname (pathname) {
  if (pathname.substr(0, 1) !== '/') {
    pathname = `/${pathname}`
  }

  return pathname
}

// createQuery will use the URLSearchParams constructor function to turn a string, array, or object into a valid
// query string.
function createQuery (query) {
  if (query instanceof URLSearchParams) {
    query = query.toString()
  }

  if (typeof query === 'object' && !Array.isArray(query)) {
    const q = []

    for (const [key, values] of Object.entries(query)) {
      if (Array.isArray(values)) {
        for (const value of values) {
          q.push([key, value])
        }
      } else {
        q.push([key, values])
      }
    }

    query = q
  }

  if (typeof query !== 'string') {
    query = (new URLSearchParams(query)).toString()
  }

  if (query && query.substr(0, 1) !== '?') {
    query = `?${query}`
  }

  return query
}

// createHash will return a new hash string based on the string given to it.
// If a has exists then it will always be prefixed with a #, otherwise it will be an empty string.
function createHash (hash) {
  if (hash.substr(0, 1) === '#') {
    hash = hash.substr(1)
  }

  if (hash) {
    hash = `#${hash}`
  }

  return hash
}

// goToHash will attempt to scroll to the element that has an id or name attribute matching the current hash in the URL.
function goToHash () {
  return new Promise(resolve => {
    setTimeout(() => {
      let scrolled = false
      const hash = location.hash.replace('#', '')

      if (hash) {
        const node = document.getElementById(hash) || document.querySelector(`a[name="${hash}"]`)

        if (node) {
          const rect = node.getBoundingClientRect()
          const top = rect.top + window.scrollY

          scrollTo(top)

          scrolled = true
        }
      }

      resolve(scrolled)
    }, 0)
  })
}

// scrollTo is a helper function to scroll to the given vertical location and use smooth scrolling if it's supported.
function scrollTo (top) {
  if ('scrollBehavior' in document.documentElement.style) {
    window.scrollTo({
      top,
      left: 0,
      behavior: 'smooth',
    })
  } else {
    window.scrollTo(0, top)
  }
}
